import { useMemo, useState } from 'react';
import { Box, Button } from '@mui/material';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { DataGridPremium, GridColDef, GridRenderCellParams, GridRenderEditCellParams, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import { useTranslation } from 'react-i18next';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import {
  DefaultLookupEditCell,
  MappingTargetField,
  SourceColumnEditCell,
  TargetFieldCell,
  TargetFieldEditCell,
  TransformColumnHeader,
} from '@/integration/importMappingCells';

/** One row of a mapping being edited: a column of the file and the field it fills. */
export interface ImportMappingRow {
  id: string;
  sourceColumn: string;
  targetField: string;
  transformChain: string;
  defaultValue: string;
}

interface ImportMappingGridProps {
  rows: ImportMappingRow[];
  onRowsChange: (rows: ImportMappingRow[]) => void;
  /** New above the grid: the caller adds an empty row. */
  onAdd: () => void;
  /** The file's column headers. */
  sourceColumns: string[];
  /** The fields the entity accepts. */
  fields: MappingTargetField[];
}

const NO_SELECTION: GridRowSelectionModel = { type: 'include', ids: new Set() };

/**
 * A column-to-field mapping that lives in the caller's state, not in the database: the online
 * import panel maps a file without saving anything. The cell editors are the Import Profile
 * editor's (importMappingCells). The columns share the width they get, so the grid fits the panel.
 * New and Delete sit above it and rows are picked with their checkboxes, as in a child grid; nothing
 * is saved, so nothing is confirmed.
 */
export function ImportMappingGrid({ rows, onRowsChange, onAdd, sourceColumns, fields }: ImportMappingGridProps) {
  const { t } = useTranslation();
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>(NO_SELECTION);

  const deleteSelected = () => {
    onRowsChange(rows.filter((r) => !rowSelectionModel.ids.has(r.id)));
    setRowSelectionModel(NO_SELECTION);
  };

  const columns = useMemo<GridColDef<ImportMappingRow>[]>(() => [
    {
      field: 'sourceColumn',
      headerName: t('onlineImport.column.source', { defaultValue: 'Column in file' }),
      flex: 1,
      minWidth: 120,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams) => <SourceColumnEditCell {...params} sourceColumns={sourceColumns} />,
    },
    {
      field: 'targetField',
      headerName: t('onlineImport.column.target', { defaultValue: 'Field' }),
      flex: 1.2,
      minWidth: 140,
      editable: true,
      renderCell: (params: GridRenderCellParams) => <TargetFieldCell {...params} entityFields={fields} />,
      renderEditCell: (params: GridRenderEditCellParams) => <TargetFieldEditCell {...params} entityFields={fields} />,
    },
    {
      field: 'transformChain',
      headerName: t('onlineImport.column.transform', { defaultValue: 'Transform' }),
      flex: 1,
      minWidth: 110,
      editable: true,
      renderHeader: () => <TransformColumnHeader label={t('onlineImport.column.transform', { defaultValue: 'Transform' })} />,
    },
    {
      field: 'defaultValue',
      headerName: t('onlineImport.column.default', { defaultValue: 'Default when created' }),
      flex: 1,
      minWidth: 110,
      editable: true,
      renderEditCell: (params: GridRenderEditCellParams) => {
        const fieldMeta = fields.find((f) => f.name === params.row.targetField);
        return fieldMeta?.referencedEntity && fieldMeta.referencedModule
          ? <DefaultLookupEditCell {...params} fieldMeta={fieldMeta} />
          : undefined;
      },
    },
  ], [t, sourceColumns, fields]);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Button size="small" variant="outlined" startIcon={<Add />} onClick={onAdd} sx={{ textTransform: 'none' }}>
          {t('common.new', { defaultValue: 'New' })}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<Delete />}
          onClick={deleteSelected}
          disabled={rowSelectionModel.ids.size === 0}
          sx={{ textTransform: 'none' }}
        >
          {t('common.delete', { defaultValue: 'Delete' })}
        </Button>
      </Box>
      <DataGridPremium
        rows={rows}
        columns={columns}
        density="compact"
        rowHeight={GRID_ROW_HEIGHT}
        editMode="cell"
        processRowUpdate={(updated: ImportMappingRow) => {
          onRowsChange(rows.map((r) => (r.id === updated.id ? updated : r)));
          return updated;
        }}
        hideFooter
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={rowSelectionModel}
        onRowSelectionModelChange={setRowSelectionModel}
        sx={{ border: 'none', maxHeight: 360 }}
      />
    </Box>
  );
}
